<script lang="ts" setup>
	import type { WaitingPeriodContext } from './toggle-waiting-period.vue'
	import { useResourcesApi } from '~/services/apis/resources'

	const { useDefaultClass = true, ...props } = defineProps<{
		productDetails?: ProductsResponse | null
		waitingPeriodFee?: number
		loading?: boolean
		wrapperClass?: ClassContext
		withWaitingPeriod?: WaitingPeriodContext
		useDefaultClass?: boolean
	}>()

	defineEmits<{
		(arg: 'toggle-waiting-period', param: WaitingPeriodContext): void
	}>()
	const { internationalNoFormat, currencyToNaira } = useCurrency()
	const { productCategoryType, toLower } = useUtils()
	const productInfo = computed(() => props?.productDetails ?? null)
	const priceVariation = computed(() => productInfo.value?.prices ?? [])
	const productCode = computed(() => productInfo.value?.code_name ?? 0)
	const productCategoryName = computed(() => toLower(productInfo.value?.productCategory?.name ?? ''))
	const categoryType = computed(() => productCategoryType(productCategoryName.value, productCode.value))
	const productName = computed(() => productInfo.value?.name ?? 'N/A')
	const isDynamicPrice = computed(() => productInfo.value?.price?.is_dynamic_price ?? false)
	const productPrice = computed(() => Number(productInfo.value?.price?.market_price ?? 0))
	const productCaptionTag = computed(() => {
		let label = categoryType.value?.isAuto
			? `/vehicle/${toLower(productInfo.value?.price?.cover_period ?? 'annually')}`
			: ''
		let price = ''
		if (categoryType.value?.isComprehensive || categoryType.value?.isGadget) {
			price = `Starts from ${
				isDynamicPrice.value ? `${productPrice.value}%` : currencyToNaira(productPrice.value)
			} of ${productName.value} value`
			label = categoryType.value?.isGadget
				? '/gadget yearly'
				: `/vehicle/${toLower(productInfo.value?.price?.cover_period ?? 'annually')}`
		}
		if (categoryType.value?.isLaptopCover) {
			price = `${productPrice.value}% of Laptop value`
			label = '/per annum'
		}
		if (categoryType.value?.isAuto && !categoryType.value?.isComprehensive) {
			price = currencyToNaira(productPrice.value)
		}
		if (categoryType.value.isHealth) {
			label = '/person bi-annually'
			price = currencyToNaira(productPrice.value)
		}
		if (categoryType.value?.isTravel) {
			price = 'Premium based on'
			label = 'Duration of travel, Country, Age'
		}
		return {
			title: price,
			label
		}
	})

	type requiredContext = {
		name: string
		is_dynamic_price?: boolean
		market_price: string
	}
	const gadgetRates = shallowRef<requiredContext[]>([])

	const productVariations = computed<requiredContext[]>(() => {
		return (
			priceVariation.value.map(item => ({
				name: String(item.cover_period),
				market_price: String(item.market_price),
				is_dynamic_price: Boolean(item.is_dynamic_price)
			})) || []
		)
	})
	// TODO: Remember to reach out to backend to filter this out and make it proper
	const variations = computed(() => {
		return categoryType.value?.isComprehensive
			? productVariations.value
			: categoryType.value?.isGadget
				? gadgetRates.value
				: []
	})
	const { getGadgetCategories } = useResourcesApi()
	watch(
		() => categoryType.value?.isGadget,
		async isTrue => {
			if (isTrue && props.productDetails?.id) {
				const list = await getGadgetCategories(props.productDetails.id)
				gadgetRates.value = list.map(item => ({
					name: String(item.label),
					market_price: String(item.market_price ?? 0),
					is_dynamic_price: true
				}))
			}
		},
		{ immediate: true }
	)
</script>

<template>
	<div>
		<div
			v-if="!loading"
			class="flex flex-col items-center justify-center gap-x-1 rounded-lg border-[0.4px] sm:space-x-2"
			:class="[(useDefaultClass && 'min-h-[4rem] border-purple-500 bg-purple-50 px-3 py-2') || '', wrapperClass]"
		>
			<div class="flex items-center gap-x-1">
				<slot>
					<span class="text-lg font-bold text-gray-600">
						{{ productCaptionTag?.title }}
					</span>

					<span class="text-sm font-medium text-gray-500 sm:leading-[18px]">
						{{ productCaptionTag.label }}
					</span>
				</slot>

				<a-tooltip
					v-if="categoryType?.isComprehensive || (categoryType?.isGadget && !categoryType.isLaptopCover)"
					color="white"
					trigger="click"
					placement="bottom"
				>
					<button type="button" name="Tip">
						<lazy-tools-icon
							icon="warning"
							dir="fill"
							aria-label="note"
							path-class="fill-gray-500"
							:height="18"
							:width="18"
						/>
					</button>

					<template #title>
						<div class="min-w-[200px] p-2">
							<h3 class="mb-3 text-sm font-medium text-gray-700">
								{{
									categoryType?.isComprehensive
										? 'Vehicle Value'
										: categoryType?.isGadget
											? 'Gadget Value'
											: ''
								}}
							</h3>

							<ul class="space-y-3">
								<li
									v-for="(item, index) in variations"
									:key="index"
									class="flex justify-between text-xs font-normal text-gray-500"
								>
									<span class="lowercase">{{ item?.name ?? 'N/A' }}</span>
									<span class="font-semibold text-gray-600">
										{{
											item?.is_dynamic_price
												? `${parseFloat(String(item.market_price)) ?? ''}%`
												: internationalNoFormat(item.market_price ?? 0, '₦')
										}}
									</span>
								</li>
							</ul>
						</div>
					</template>
				</a-tooltip>
			</div>
		</div>
		<a-skeleton-button v-else size="large" class="mx-auto h-12 max-w-[250px] rounded-lg" block active loading />
	</div>
</template>
